import type { Case } from '@/lib/cases'
import type {
  DocumentRecordStatus,
  SourceDocumentRecord,
  StructuredDocumentRecord,
  SupportedDocumentType,
} from '@/features/case-workspace/types'
import { DOCUMENT_DEFINITIONS, DOCUMENT_TYPE_ORDER } from '@/features/case-workspace/schemas'

export type TransmitDocumentAction = 'ace' | 'email' | 'download'
export type TransmitDocumentReadiness = 'ready' | 'attention_needed' | 'unavailable'

export type TransmitDocument = {
  id: string
  caseId: string
  documentType: SupportedDocumentType
  title: string
  pdfUrl: string | null
  sourceType: 'structured_record'
  readinessStatus: TransmitDocumentReadiness
  lastGeneratedAt: string | null
  availableActions: TransmitDocumentAction[]
  summary: string
  generationNote: string
}

const DEMO_CBP_7501_PDF_URL = '/demo/cbp-form-7501-demo.pdf'

function escapePdfText(value: string) {
  // Strip non-ASCII so the PDF stays pure ASCII and xref byte offsets
  // stay valid. The generated PDF is a human-readable stub — full
  // Unicode support would require a CIDFont + ToUnicode CMap.
  return value
    .replace(/[^\x20-\x7E]/g, '?')
    .replace(/\\/g, '\\\\')
    .replace(/\(/g, '\\(')
    .replace(/\)/g, '\\)')
}

function buildPdfDataUri(lines: string[]) {
  const content = [
    'BT',
    '/F1 12 Tf',
    '50 770 Td',
    ...lines.flatMap((line, index) => [
      index === 0 ? `(${escapePdfText(line)}) Tj` : `0 -18 Td (${escapePdfText(line)}) Tj`,
    ]),
    'ET',
  ].join('\n')

  const objects = [
    '1 0 obj\n<< /Type /Catalog /Pages 2 0 R >>\nendobj\n',
    '2 0 obj\n<< /Type /Pages /Kids [3 0 R] /Count 1 >>\nendobj\n',
    '3 0 obj\n<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>\nendobj\n',
    `4 0 obj\n<< /Length ${content.length} >>\nstream\n${content}\nendstream\nendobj\n`,
    '5 0 obj\n<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>\nendobj\n',
  ]

  let pdf = '%PDF-1.4\n'
  const offsets: number[] = [0]

  for (const object of objects) {
    offsets.push(pdf.length)
    pdf += object
  }

  const xrefOffset = pdf.length
  pdf += `xref\n0 ${objects.length + 1}\n`
  pdf += '0000000000 65535 f \n'
  for (let index = 1; index <= objects.length; index += 1) {
    pdf += `${String(offsets[index]).padStart(10, '0')} 00000 n \n`
  }
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF`

  // Encode to UTF-8 bytes first — btoa() throws on non-Latin-1 chars
  // which can appear in importer names or commodity descriptions.
  const bytes = new TextEncoder().encode(pdf)
  let binary = ''
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return `data:application/pdf;base64,${btoa(binary)}`
}

function readinessForStatus(status: DocumentRecordStatus): TransmitDocumentReadiness {
  if (status === 'complete' || status === 'ready') return 'ready'
  if (status === 'needs_attention') return 'attention_needed'
  return 'unavailable'
}

function pdfLinesForRecord(kase: Case, record: StructuredDocumentRecord, linkedSources: SourceDocumentRecord[]) {
  const definition = DOCUMENT_DEFINITIONS[record.documentType]
  const populatedFieldLines = definition.sections.flatMap((section) =>
    section.fields
      .map((field) => {
        const value = record.fields[field.key]?.displayValue || record.fields[field.key]?.value
        return value ? `${field.label}: ${value}` : null
      })
      .filter((line): line is string => Boolean(line)),
  )

  const linkedSourceLabel =
    linkedSources.length > 0
      ? `Linked source files: ${linkedSources.map((source) => source.title).join(', ')}`
      : 'Linked source files: none'

  return [
    `${record.title}`,
    `Case: ${kase.case_number}`,
    `Status: ${record.status.replace(/_/g, ' ')}`,
    `Prepared: ${record.lastUpdatedAt.slice(0, 10)}`,
    linkedSourceLabel,
    ...populatedFieldLines.slice(0, 18),
  ]
}

function generationNote(definitionLabel: string, readinessStatus: TransmitDocumentReadiness) {
  if (readinessStatus === 'ready') {
    return `${definitionLabel} is available in PDF form for review and transmission.`
  }
  if (readinessStatus === 'attention_needed') {
    return `${definitionLabel} still needs attention before a final PDF is available.`
  }
  return `${definitionLabel} does not have a final PDF available yet.`
}

export function buildTransmitDocuments(params: {
  kase: Case
  sourceDocuments: SourceDocumentRecord[]
  structuredDocuments: StructuredDocumentRecord[]
}) {
  const { kase, sourceDocuments, structuredDocuments } = params

  return structuredDocuments
    .slice()
    .sort((left, right) => {
      const leftOrder = DOCUMENT_TYPE_ORDER.indexOf(left.documentType)
      const rightOrder = DOCUMENT_TYPE_ORDER.indexOf(right.documentType)
      if (leftOrder !== rightOrder) return leftOrder - rightOrder
      return left.title.localeCompare(right.title)
    })
    .map((record) => {
      const readinessStatus = readinessForStatus(record.status)
      const linkedSources = sourceDocuments.filter((source) => source.linkedStructuredDocumentId === record.id)
      const pdfUrl =
        readinessStatus === 'ready'
          ? record.documentType === 'cbp_7501'
            ? DEMO_CBP_7501_PDF_URL
            : buildPdfDataUri(pdfLinesForRecord(kase, record, linkedSources))
          : null

      return {
        id: record.id,
        caseId: record.caseId,
        documentType: record.documentType,
        title: `${DOCUMENT_DEFINITIONS[record.documentType].label} PDF`,
        pdfUrl,
        sourceType: 'structured_record',
        readinessStatus,
        lastGeneratedAt: readinessStatus === 'ready' ? record.lastUpdatedAt : null,
        availableActions: [
          'ace',
          'email',
          ...(pdfUrl ? (['download'] as const) : []),
        ],
        summary: DOCUMENT_DEFINITIONS[record.documentType].shortDescription,
        generationNote: generationNote(DOCUMENT_DEFINITIONS[record.documentType].label, readinessStatus),
      } satisfies TransmitDocument
    })
}

export async function requestEmailTransmitPlaceholder(document: TransmitDocument) {
  return {
    ok: true,
    message: `${document.title} is ready for the email transmission workflow. TODO: connect this action to recipient selection and outbound delivery.`,
  }
}

export function downloadTransmitDocument(document: TransmitDocument) {
  if (!document.pdfUrl) {
    return {
      ok: false,
      message: `${document.title} is not available to download yet.`,
    }
  }

  const anchor = window.document.createElement('a')
  anchor.href = document.pdfUrl
  anchor.download = `${document.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`
  window.document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()

  return {
    ok: true,
    message: `${document.title} download started.`,
  }
}
